/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import {
  SafeAreaView,
  ScrollView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  Image,
  Alert,
  Linking,
  StatusBar,
  ActivityIndicator,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width } = Dimensions.get('window');

const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  companyProfile: `${API_BASE_URL}/api/data/company_profiles`,
  vendorsData: `${API_BASE_URL}/api/data/vendors`, // dynamic endpoint
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        config.params = { ...config.params, company_id: parseInt(companyId) };
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const SubcontractorDetailScreen = ({ navigation, route }) => {
  const { subcontractorId } = route.params || {};

  const [companyLogo, setCompanyLogo] = useState(null);
  const [subcontractor, setSubcontractor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('Overview');

  useFocusEffect(
    useCallback(() => {
      loadCompanyLogo();
    }, [])
  );

  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setCompanyLogo(profile.logo || null);
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        const response = await api.get(API_ENDPOINTS.companyProfile, {
          params: { company_id: companyId },
        });
        if (response.data?.success && response.data.data?.length > 0) {
          const latestProfile = response.data.data[0];
          setCompanyLogo(latestProfile.logo || null);
          await AsyncStorage.setItem('company_profile', JSON.stringify(latestProfile));
        }
      }
    } catch (error) {
      console.warn('Error loading company logo:', error);
    }
  };

  const fetchSubcontractor = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('🔍 Fetching subcontractor with ID:', subcontractorId);
      const url = `${API_ENDPOINTS.vendorsData}/${subcontractorId}`;
      console.log('📡 Request URL:', url);
      const response = await api.get(url);
      console.log('✅ API response:', response.data);

      if (response.data?.success) {
        const vendor = response.data.data;
        const transformed = {
          id: vendor.id,
          name: vendor.company_name,
          specialization: vendor.specialization || [],
          ratings: vendor.ratings || { quality: 0, reliability: 0, cost: 0, time: 0 },
          overall: Number(vendor.overall) || 0,
          contact: {
            phone: vendor.phone,
            email: vendor.email,
            emergencyPhone: vendor.emergency_phone,
          },
          location: vendor.city && vendor.country ? `${vendor.city}, ${vendor.country}` : (vendor.address || 'Unknown'),
          status: vendor.status,
          teamSize: vendor.team_size,
          contractInfo: vendor.contract_info || {},
          companyInfo: vendor.company_info || {},
          availability: vendor.availability || {},
          recentProjects: vendor.recent_projects || [],
          notes: vendor.notes,
        };
        setSubcontractor(transformed);
      } else {
        setError('Failed to load subcontractor details');
      }
    } catch (err) {
      console.error('❌ Error fetching subcontractor:', err.message);
      if (err.response) {
        console.log('Status:', err.response.status);
        console.log('Data:', err.response.data);
      }
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (subcontractorId) {
      fetchSubcontractor();
    } else {
      setError('No subcontractor ID provided');
      setLoading(false);
    }
  }, [subcontractorId]);

  const renderRatingBar = (value, maxValue = 5) => {
    const percentage = (value / maxValue) * 100;
    return (
      <View style={styles.ratingBarContainer}>
        <View style={[styles.ratingBar, { width: `${percentage}%` }]} />
      </View>
    );
  };

  const handleCall = (phoneNumber) => {
    if (phoneNumber) Linking.openURL(`tel:${phoneNumber}`);
  };

  const handleEmail = (email) => {
    if (email) Linking.openURL(`mailto:${email}`);
  };

  const handleWebsite = (website) => {
    if (website) Linking.openURL(`https://${website}`);
  };

  const handleGoBack = () => {
    if (navigation.canGoBack()) navigation.goBack();
  };

  const renderOverviewTab = () => {
    if (!subcontractor) return null;
    const { companyInfo = {}, contact = {}, specialization = [], ratings = {}, overall, notes } = subcontractor;
    return (
      <View>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Company Information</Text>
          <View style={styles.infoGrid}>
            <View style={styles.infoItem}>
              <Ionicons name="business-outline" size={18} color="#0e4e96" />
              <View style={styles.infoTextContainer}>
                <Text style={styles.infoLabel}>Established</Text>
                <Text style={styles.infoValue}>{companyInfo.established || 'N/A'}</Text>
              </View>
            </View>
            <View style={styles.infoItem}>
              <MaterialCommunityIcons name="license" size={18} color="#0e4e96" />
              <View style={styles.infoTextContainer}>
                <Text style={styles.infoLabel}>License</Text>
                <Text style={styles.infoValue}>{companyInfo.license || 'N/A'}</Text>
              </View>
            </View>
            <View style={styles.infoItem}>
              <MaterialCommunityIcons name="shield-check-outline" size={18} color="#0e4e96" />
              <View style={styles.infoTextContainer}>
                <Text style={styles.infoLabel}>Insurance</Text>
                <Text style={styles.infoValue}>{companyInfo.insurance || 'N/A'}</Text>
              </View>
            </View>
            {companyInfo.website && (
              <TouchableOpacity style={styles.infoItem} onPress={() => handleWebsite(companyInfo.website)}>
                <Ionicons name="globe-outline" size={18} color="#0e4e96" />
                <View style={styles.infoTextContainer}>
                  <Text style={styles.infoLabel}>Website</Text>
                  <Text style={[styles.infoValue, styles.linkText]}>{companyInfo.website}</Text>
                </View>
              </TouchableOpacity>
            )}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Contact Information</Text>
          <TouchableOpacity style={styles.contactItem} onPress={() => handleCall(contact.phone)}>
            <Ionicons name="call-outline" size={18} color="#0e4e96" />
            <Text style={styles.contactText}>{contact.phone || 'N/A'}</Text>
            <Ionicons name="chevron-forward" size={16} color="#64748B" />
          </TouchableOpacity>
          <TouchableOpacity style={styles.contactItem} onPress={() => handleEmail(contact.email)}>
            <Ionicons name="mail-outline" size={18} color="#0e4e96" />
            <Text style={styles.contactText}>{contact.email || 'N/A'}</Text>
            <Ionicons name="chevron-forward" size={16} color="#64748B" />
          </TouchableOpacity>
          {contact.emergencyPhone && (
            <TouchableOpacity style={styles.contactItem} onPress={() => handleCall(contact.emergencyPhone)}>
              <MaterialCommunityIcons name="phone-alert" size={18} color="#0e4e96" />
              <Text style={styles.contactText}>Emergency: {contact.emergencyPhone}</Text>
              <Ionicons name="chevron-forward" size={16} color="#64748B" />
            </TouchableOpacity>
          )}
          <View style={styles.contactItem}>
            <Ionicons name="location-outline" size={18} color="#0e4e96" />
            <Text style={styles.contactText}>{subcontractor.location || 'N/A'}</Text>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Specializations</Text>
          <View style={styles.specializationTags}>
            {(specialization || []).map((spec, index) => (
              <View key={index} style={styles.specializationTag}>
                <Text style={styles.specializationTagText}>{spec}</Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Performance Ratings</Text>
          <View style={styles.ratingsContainer}>
            <View style={styles.overallRating}>
              <Text style={styles.overallRatingValue}>{overall?.toFixed(1) || '0.0'}</Text>
              <Text style={styles.overallRatingLabel}>Overall</Text>
            </View>
            <View style={styles.detailedRatings}>
              <View style={styles.ratingItem}>
                <Text style={styles.ratingLabel}>Quality</Text>
                <View style={styles.ratingBarWrapper}>{renderRatingBar(ratings.quality || 0)}</View>
                <Text style={styles.ratingValue}>{(ratings.quality || 0).toFixed(1)}</Text>
              </View>
              <View style={styles.ratingItem}>
                <Text style={styles.ratingLabel}>Reliability</Text>
                <View style={styles.ratingBarWrapper}>{renderRatingBar(ratings.reliability || 0)}</View>
                <Text style={styles.ratingValue}>{(ratings.reliability || 0).toFixed(1)}</Text>
              </View>
              <View style={styles.ratingItem}>
                <Text style={styles.ratingLabel}>Cost</Text>
                <View style={styles.ratingBarWrapper}>{renderRatingBar(ratings.cost || 0)}</View>
                <Text style={styles.ratingValue}>{(ratings.cost || 0).toFixed(1)}</Text>
              </View>
              <View style={styles.ratingItem}>
                <Text style={styles.ratingLabel}>Time</Text>
                <View style={styles.ratingBarWrapper}>{renderRatingBar(ratings.time || 0)}</View>
                <Text style={styles.ratingValue}>{(ratings.time || 0).toFixed(1)}</Text>
              </View>
            </View>
          </View>
        </View>

        {notes && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Notes</Text>
            <Text style={styles.notesText}>{notes}</Text>
          </View>
        )}
      </View>
    );
  };

  const renderProjectsTab = () => {
    const projects = subcontractor?.recentProjects || [];
    if (projects.length === 0) {
      return (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Recent Projects</Text>
          <Text style={styles.notesText}>No project data available.</Text>
        </View>
      );
    }
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Recent Projects</Text>
        {projects.map((project, idx) => (
          <View key={idx} style={styles.projectCard}>
            <Text style={styles.projectName}>{project.name}</Text>
            <Text style={styles.projectDetail}>Status: {project.status}</Text>
            {project.completionDate && <Text>Completed: {project.completionDate}</Text>}
            {project.startDate && <Text>Started: {project.startDate}</Text>}
            {project.expectedCompletion && <Text>Expected: {project.expectedCompletion}</Text>}
            {project.rating && (
              <View style={styles.projectRating}>
                <Ionicons name="star" size={14} color="#fdb913" />
                <Text>{project.rating}</Text>
              </View>
            )}
          </View>
        ))}
      </View>
    );
  };

  const renderContractTab = () => {
    const { contractInfo = {}, availability = {} } = subcontractor || {};
    return (
      <View>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Contract Details</Text>
          <View style={styles.infoGrid}>
            <View style={styles.infoItem}>
              <MaterialCommunityIcons name="calendar-start" size={18} color="#0e4e96" />
              <View style={styles.infoTextContainer}>
                <Text style={styles.infoLabel}>Start Date</Text>
                <Text style={styles.infoValue}>{contractInfo.startDate || 'N/A'}</Text>
              </View>
            </View>
            <View style={styles.infoItem}>
              <MaterialCommunityIcons name="calendar-end" size={18} color="#0e4e96" />
              <View style={styles.infoTextContainer}>
                <Text style={styles.infoLabel}>End Date</Text>
                <Text style={styles.infoValue}>{contractInfo.endDate || 'N/A'}</Text>
              </View>
            </View>
            <View style={styles.infoItem}>
              <MaterialCommunityIcons name="cash" size={18} color="#0e4e96" />
              <View style={styles.infoTextContainer}>
                <Text style={styles.infoLabel}>Hourly Rate</Text>
                <Text style={styles.infoValue}>{contractInfo.hourlyRate || 'N/A'}</Text>
              </View>
            </View>
            <View style={styles.infoItem}>
              <MaterialCommunityIcons name="file-document-outline" size={18} color="#0e4e96" />
              <View style={styles.infoTextContainer}>
                <Text style={styles.infoLabel}>Contract Type</Text>
                <Text style={styles.infoValue}>{contractInfo.contractType || 'N/A'}</Text>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Availability</Text>
          <View style={styles.availabilityGrid}>
            <View style={styles.availabilityItem}>
              <Text style={styles.availabilityLabel}>Current Capacity</Text>
              <Text style={styles.availabilityValue}>{availability.currentCapacity || 'N/A'}</Text>
            </View>
            <View style={styles.availabilityItem}>
              <Text style={styles.availabilityLabel}>Next Available</Text>
              <Text style={styles.availabilityValue}>{availability.nextAvailable || 'N/A'}</Text>
            </View>
            <View style={styles.availabilityItem}>
              <Text style={styles.availabilityLabel}>Working Hours</Text>
              <Text style={styles.availabilityValue}>{availability.workingHours || 'N/A'}</Text>
            </View>
            <View style={styles.availabilityItem}>
              <Text style={styles.availabilityLabel}>Working Days</Text>
              <Text style={styles.availabilityValue}>
                {(availability.workingDays || []).join(', ') || 'N/A'}
              </Text>
            </View>
          </View>
        </View>
      </View>
    );
  };

  const renderTabContent = () => {
    if (!subcontractor) return null;
    switch (activeTab) {
      case 'Overview': return renderOverviewTab();
      case 'Projects': return renderProjectsTab();
      case 'Contract': return renderContractTab();
      default: return renderOverviewTab();
    }
  };

  const renderHeader = () => (
    <View style={styles.header}>
      <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
        <Ionicons name="chevron-back" size={24} color="#fff" />
      </TouchableOpacity>
      <View style={styles.headerCenter}>
        {companyLogo ? (
          <Image source={{ uri: companyLogo || undefined }} style={styles.headerLogo} />
        ) : (
          <MaterialCommunityIcons name="office-building" size={30} color="#fff" />
        )}
        <Text style={styles.headerTitle}>Subcontractor Details</Text>
      </View>
      <TouchableOpacity
        style={styles.headerButton}
        onPress={() => navigation.navigate('AddEditSubcontractor', { subcontractor, isEdit: true })}
      >
        <Ionicons name="create-outline" size={24} color="#fff" />
      </TouchableOpacity>
    </View>
  );

  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
        {renderHeader()}
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading subcontractor details...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (error || !subcontractor) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
        {renderHeader()}
        <View style={styles.errorContainer}>
          <MaterialCommunityIcons name="alert-circle-outline" size={48} color="#EF4444" />
          <Text style={styles.errorText}>{error || 'Subcontractor not found'}</Text>
          <TouchableOpacity style={styles.retryButton} onPress={fetchSubcontractor}>
            <Text style={styles.retryButtonText}>Retry</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
      {renderHeader()}

      <View style={styles.subcontractorHeader}>
        <View style={styles.subcontractorInfo}>
          <Text style={styles.subcontractorName}>{subcontractor.name}</Text>
          <Text style={styles.subcontractorLocation}>
            <Ionicons name="location-outline" size={14} color="#64748B" /> {subcontractor.location}
          </Text>
          <Text style={styles.subcontractorTeamSize}>
            <Ionicons name="people-outline" size={14} color="#64748B" /> Team: {subcontractor.teamSize}
          </Text>
        </View>
        <View
          style={[
            styles.statusBadge,
            subcontractor.status === 'Active' ? styles.activeStatus : styles.inactiveStatus,
          ]}
        >
          <Text style={styles.statusText}>{subcontractor.status}</Text>
        </View>
      </View>

      <View style={styles.tabContainer}>
        {['Overview', 'Projects', 'Contract'].map((tab) => (
          <TouchableOpacity
            key={tab}
            style={[styles.tabButton, activeTab === tab && styles.activeTabButton]}
            onPress={() => setActiveTab(tab)}
          >
            <Text style={[styles.tabText, activeTab === tab && styles.activeTabText]}>{tab}</Text>
          </TouchableOpacity>
        ))}
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {renderTabContent()}
        <View style={styles.bottomPadding} />
      </ScrollView>

      <View style={styles.actionButtons}>
        <TouchableOpacity style={styles.actionButton} onPress={() => handleCall(subcontractor.contact?.phone)}>
          <Ionicons name="call" size={20} color="#fff" />
          <Text style={styles.actionButtonText}>Call</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.actionButton} onPress={() => handleEmail(subcontractor.contact?.email)}>
          <Ionicons name="mail" size={20} color="#fff" />
          <Text style={styles.actionButtonText}>Email</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.actionButton, styles.primaryActionButton]}
          onPress={() => navigation.navigate('ProjectAssignment', { subcontractor })}
        >
          <MaterialCommunityIcons name="clipboard-text" size={20} color="#fff" />
          <Text style={styles.actionButtonText}>Assign</Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  // ... (styles unchanged – copy from your existing file)
  safeArea: { flex: 1, backgroundColor: '#4A90E2' },
  header: {
    backgroundColor: '#4A90E2',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: { padding: 5 },
  headerCenter: { flexDirection: 'row', alignItems: 'center' },
  headerLogo: { width: 30, height: 30, borderRadius: 15, resizeMode: 'cover' },
  headerTitle: { color: '#fff', fontSize: 20, fontWeight: 'bold', marginLeft: 10 },
  headerButton: { padding: 5 },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#f8fafc' },
  loadingText: { marginTop: 10, fontSize: 16, color: '#64748B' },
  errorContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20, backgroundColor: '#f8fafc' },
  errorText: { fontSize: 16, color: '#EF4444', textAlign: 'center', marginVertical: 10 },
  retryButton: { backgroundColor: '#0e4e96', paddingVertical: 10, paddingHorizontal: 20, borderRadius: 8 },
  retryButtonText: { color: '#fff', fontWeight: '600' },
  subcontractorHeader: {
    backgroundColor: '#fff',
    padding: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 10,
    elevation: 2,
  },
  subcontractorInfo: { flex: 1 },
  subcontractorName: { fontSize: 22, fontWeight: 'bold', color: '#0f172a', marginBottom: 5 },
  subcontractorLocation: { fontSize: 14, color: '#64748B', marginBottom: 3 },
  subcontractorTeamSize: { fontSize: 14, color: '#64748B' },
  statusBadge: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 4 },
  activeStatus: { backgroundColor: '#10B981' },
  inactiveStatus: { backgroundColor: '#64748B' },
  statusText: { color: '#fff', fontSize: 12, fontWeight: 'bold' },
  tabContainer: {
    backgroundColor: '#fff',
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  tabButton: { flex: 1, paddingVertical: 15, alignItems: 'center', borderBottomWidth: 2, borderBottomColor: 'transparent' },
  activeTabButton: { borderBottomColor: '#0e4e96' },
  tabText: { fontSize: 16, color: '#64748B', fontWeight: '500' },
  activeTabText: { color: '#0e4e96', fontWeight: 'bold' },
  content: { flex: 1, backgroundColor: '#f8fafc' },
  section: {
    backgroundColor: '#fff',
    marginHorizontal: 15,
    marginTop: 15,
    borderRadius: 12,
    padding: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 10,
    elevation: 2,
  },
  sectionTitle: { fontSize: 18, fontWeight: '600', color: '#0f172a', marginBottom: 15 },
  infoGrid: { gap: 15 },
  infoItem: { flexDirection: 'row', alignItems: 'center' },
  infoTextContainer: { marginLeft: 12, flex: 1 },
  infoLabel: { fontSize: 12, color: '#64748B', marginBottom: 2 },
  infoValue: { fontSize: 14, color: '#0f172a', fontWeight: '500' },
  linkText: { color: '#0e4e96', textDecorationLine: 'underline' },
  contactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  contactText: { fontSize: 14, color: '#0f172a', marginLeft: 12, flex: 1 },
  specializationTags: { flexDirection: 'row', flexWrap: 'wrap' },
  specializationTag: {
    backgroundColor: '#f1f5f9',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 4,
    marginRight: 8,
    marginBottom: 8,
  },
  specializationTagText: { color: '#0e4e96', fontSize: 12, fontWeight: '500' },
  ratingsContainer: { flexDirection: 'row' },
  overallRating: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#f1f5f9',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 20,
  },
  overallRatingValue: { fontSize: 24, fontWeight: 'bold', color: '#0e4e96' },
  overallRatingLabel: { fontSize: 12, color: '#64748B', textAlign: 'center' },
  detailedRatings: { flex: 1 },
  ratingItem: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  ratingLabel: { width: 70, fontSize: 14, color: '#0f172a' },
  ratingBarWrapper: { flex: 1, marginHorizontal: 10 },
  ratingBarContainer: { height: 6, backgroundColor: '#e2e8f0', borderRadius: 3 },
  ratingBar: { height: '100%', backgroundColor: '#0e4e96', borderRadius: 3 },
  ratingValue: { width: 30, fontSize: 14, fontWeight: 'bold', color: '#0f172a', textAlign: 'right' },
  notesText: { fontSize: 14, color: '#0f172a', lineHeight: 20 },
  projectCard: { backgroundColor: '#f8fafc', borderRadius: 8, padding: 12, marginBottom: 10 },
  projectName: { fontSize: 16, fontWeight: '600', color: '#0f172a', marginBottom: 4 },
  projectDetail: { fontSize: 12, color: '#64748B' },
  projectRating: { flexDirection: 'row', alignItems: 'center', marginTop: 4 },
  availabilityGrid: { gap: 15 },
  availabilityItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  availabilityLabel: { fontSize: 14, color: '#64748B' },
  availabilityValue: { fontSize: 14, color: '#0f172a', fontWeight: '500' },
  actionButtons: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    paddingHorizontal: 15,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
  },
  actionButton: {
    backgroundColor: '#0e4e96',
    paddingVertical: 12,
    paddingHorizontal: 15,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    marginHorizontal: 5,
  },
  primaryActionButton: { backgroundColor: '#fdb913' },
  actionButtonText: { color: '#fff', fontWeight: '600', fontSize: 14, marginLeft: 5 },
  bottomPadding: { height: 20 },
});

export default SubcontractorDetailScreen;